import { useCachedApi, generateCacheKey } from "sj-ui-toolkit/src/api/cache";
import { transformCoursesToRankMap } from "@/constants/assignmentsReport.constants";
import { fetchLearningActivityStats } from "@/api/dashboard";
import type { LearningActivityStats } from "@/api/dashboard";
import { getLevels } from "sj-ui-toolkit/src/api";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import {
  loadLevelsVP,
  loadUsageReport,
  type ReportParams,
  type UsageResponse,
  type LessonLevelData,
  type CourseData,
} from "@/api/reports";
import { loadActiveAssignments, type AssignmentData } from "@/api/assignments";
import { loadActiveAssessments, type AssessmentData } from "@/api/assessments";

// ─── Types ───────────────────────────────────────────────────────────────────

interface RankingsData {
  coursesByRank: Record<string, CourseData>;
  lessonLevels: Record<string, LessonLevelData>;
}

export type LastYearParams = ReportParams;

// ─── Fetchers ────────────────────────────────────────────────────────────────

const fetchRankingsData = async (lastYearParams: LastYearParams): Promise<RankingsData> => {
  const [levelsData, levelsVPData] = await Promise.all([getLevels(), loadLevelsVP(lastYearParams)]);

  let coursesByRank: Record<string, CourseData> = {};
  if (levelsData?.courses && Array.isArray(levelsData.courses)) {
    coursesByRank = transformCoursesToRankMap(levelsData.courses) as Record<string, CourseData>;
  } else if (levelsData && typeof levelsData === "object" && "coursesByRank" in levelsData) {
    coursesByRank = (levelsData as { coursesByRank: Record<string, CourseData> }).coursesByRank;
  }

  return { coursesByRank, lessonLevels: levelsVPData.lessonLevels || {} };
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

export const useAdminRankingsData = (lastYearParams: LastYearParams) =>
  useCachedApi<RankingsData>(
    generateCacheKey("adminRankingsData", lastYearParams),
    () => fetchRankingsData(lastYearParams),
    { staleTime: 5 * 60, refetchOnMount: false }
  );

// Usage data (incl. license counts) is tenant-specific, so the cache key must include the
// selected tenant — otherwise switching tenants can reuse the previous tenant's counts.
export const useAdminUsageData = (lastYearParams: LastYearParams) =>
  useCachedApi<UsageResponse>(
    generateCacheKey("adminUsageData", lastYearParams, getCurrentSelectedTenant()),
    () => loadUsageReport(lastYearParams),
    { staleTime: 5 * 60, refetchOnMount: false }
  );

export const useAdminLearningActivityStats = () =>
  useCachedApi<LearningActivityStats>(
    generateCacheKey("adminLearningActivityStats"),
    fetchLearningActivityStats,
    { staleTime: 5 * 60, refetchOnMount: false }
  );

export const useAdminActiveAssignments = (tenantName: string) =>
  useCachedApi<AssignmentData[]>(
    generateCacheKey("adminActiveAssignments", tenantName),
    () => loadActiveAssignments(tenantName, 4),
    { staleTime: 2 * 60, refetchOnMount: true }
  );

export const useAdminActiveAssessments = (tenantName: string) =>
  useCachedApi<AssessmentData[]>(
    generateCacheKey("adminActiveAssessments", tenantName),
    () => loadActiveAssessments(tenantName, 4),
    { staleTime: 2 * 60, refetchOnMount: true }
  );
